
import React, { useMemo } from 'react';
import { InterviewExpanded } from '../InterviewExpanded';
import { Interview } from '@/types/interview';

interface ExpandedContentProps {
  isExpanded: boolean;
  aiSummary: string | string[];
  aiResponse: string;
  aiChat: any;
  onAiQuestion: (question: string) => void;
  onAiResponse: (response: string) => void;
  summary_status?: 'pending' | 'generating' | 'completed' | 'error';
  summary_error?: string | null;
  context?: string;
  question?: string;
  answer?: string;
  intervieweeName?: string;
  title?: string;
  company?: string;
  interviewId?: string;
}

const ExpandedContent: React.FC<ExpandedContentProps> = ({
  isExpanded,
  aiSummary,
  aiResponse,
  aiChat,
  onAiQuestion,
  onAiResponse,
  summary_status,
  summary_error,
  context,
  question,
  answer,
  intervieweeName,
  title,
  company,
  interviewId
}) => {
  if (!isExpanded) return null;
  
  // Process aiSummary for consistent format
  const processedSummary = useMemo(() => {
    if (!aiSummary) return [];
    
    if (typeof aiSummary === 'string') {
      if (aiSummary.trim().length === 0) return [];
      
      try {
        // Try to parse the string as JSON in case it's a stringified array
        const parsedSummary = JSON.parse(aiSummary);
        if (Array.isArray(parsedSummary)) {
          return parsedSummary.filter(item => !!item && item.trim().length > 0);
        }
      } catch (e) {
        // Not valid JSON, continue with normal string processing
      }
      
      // If string contains line breaks or bullet points, convert to array
      if (aiSummary.includes('\n') || aiSummary.includes('•') || aiSummary.includes('-')) {
        return aiSummary
          .split(/\n|•|-/)
          .map(point => point.trim())
          .filter(Boolean);
      }
      
      // Single string with content, make it an array with one item
      return [aiSummary];
    }
    
    if (Array.isArray(aiSummary)) {
      // Ensure all array items are valid strings
      return aiSummary
        .filter(point => point !== null && point !== undefined)
        .map(point => String(point))
        .filter(point => point.trim().length > 0);
    }
    
    return [];
  }, [aiSummary]);
  
  // Debug log to check what's being passed
  console.log('ExpandedContent props:', { 
    aiSummary, 
    processedSummary,
    summary_status,
    hasAiSummary: !!aiSummary,
    aiSummaryLength: Array.isArray(aiSummary) ? aiSummary.length : 
                     typeof aiSummary === 'string' ? aiSummary.length : 0,
    aiSummaryType: typeof aiSummary,
    aiSummaryIsArray: Array.isArray(aiSummary),
  });
  
  const interview: Interview = {
    id: interviewId || '',
    aiSummary: processedSummary,
    context,
    question,
    answer,
    intervieweeName,
    title,
    company,
    summary_status,
    summary_error
  };

  return (
    <InterviewExpanded
      interview={interview}
    />
  );
};

export default ExpandedContent;
